// Coach details interface

import { useMemo, useRef, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useFirebase } from './FirebaseContextProvider';
import { ParentStatuses } from '../utils/enums';
import UserCardTemplate from './UserCardTemplate';
import TeamsList from './TeamsList';
import TournamentsList from './TournamentsList';
import PropItem from './PropItem';
import PropItemsBlock from './PropItemsBlock';
import CoachCardEdit from './CoachCardEdit';
import PaymentsList from './PaymentsList';
import ParentsList from './ParentsList';
import GuidedTour from './GuidedTour';
import placeholderPerson from '../assets/images/placeholder-person.png';

const steps = [
  {
    target: '.tabs-header-item-tournaments',
    title: 'Tournaments',
    content: 'Browse every tournament available to your teams here',
  },
  {
    target: '.tabs-header-item-teams',
    title: 'Your teams',
    content: 'Here are the teams you coach',
  },
  {
    target: '.tabs-header-item-payments',
    title: 'Payments',
    content: 'Track and manage your payments here',
  },
  {
    target: '.tabs-header-item-profile',
    title: 'Your profile',
    content: 'Your profile lives here — edit your info and contact details',
  },
];

const stepsTournaments = [
  {
    target: '.tournament:first-of-type',
    title: 'Tournament',
    content:
      'This tournament is available for your team. Click to see the details',
  },
];

export default function CoachCard({ coach }) {
  const { user, userIsAdmin, userIsCoach, parentsByPlayerId, tournaments } =
    useFirebase();
  const { id, name, phone, email } = coach;


  const [run, setRun] = useState(false);

  const isTheUser = user.id === id;

  // Tabs are hash-driven and only the active one is mounted, so the tour steps
  // follow the open tab
  const { hash } = useLocation();
  const onTournamentsTab = hash === '#tournaments';

  useEffect(() => {
    if (user.name) setRun(true);
  }, [user.name]);

  // The link document names the coaches of the player's teams, so the question
  // is answered off the links themselves. It used to walk `users` to find out
  // whose player this was, and a player document that had not resolved dropped
  // silently out of the count.
  const hasPendingParentsNow = useMemo(() => {
    for (const parents of parentsByPlayerId.values()) {
      for (const link of parents) {
        if (link.status !== ParentStatuses.PENDING) continue;
        if (link.coachIds?.includes(id)) return true;
      }
    }
    return false;
  }, [parentsByPlayerId, id]);

  const hasPendingParents = useRef(false);
  if (hasPendingParentsNow) hasPendingParents.current = true;

  return (
    <GuidedTour
      steps={
        userIsCoach && isTheUser
          ? onTournamentsTab
            ? stepsTournaments
            : steps
          : []
      }
      // Wait for the tournaments to load, otherwise the step finds no row
      run={run && (!onTournamentsTab || tournaments.length > 0)}
      tourKey={onTournamentsTab ? 'coach-tournaments-v1' : 'coach-card-v2'}
    >
      <UserCardTemplate
        header='Coach'
        className='coach-card'
        backBtn={!isTheUser}
        isTheUser={isTheUser}
        imgPath={coach.imgPath}
        imgThumbPath={coach.imgThumbPath}
        imgTitle={name}
        name={name}
        placeholder={placeholderPerson}
        details={
          <PropItemsBlock>
            <PropItem prop='Phone' value={phone} />
            <PropItem prop='e-mail' value={email} />
          </PropItemsBlock>
        }
        editForm={(userIsAdmin || isTheUser) && <CoachCardEdit coach={coach} />}
        tabs={[
          {
            title: 'Tournaments',
            hash: '#tournaments',
            component: <TournamentsList userId={id} />,
          },
          {
            title: 'Teams',
            hash: '#teams',
            component: <TeamsList userId={id} />,
          },
          ...((isTheUser || userIsAdmin) && hasPendingParents.current
            ? [
                {
                  title: 'Approvals',
                  hash: '#approvals',
                  component: <ParentsList coachId={id} />,
                },
              ]
            : []),
          ...(isTheUser
            ? [
                {
                  title: 'Payments',
                  hash: '#payments',
                  component: <PaymentsList />,
                },
              ]
            : []),
        ]}
      />
    </GuidedTour>
  );
}
